import React from "react";
import { IonBadge, IonText } from "@ionic/react";

import { EstadoRutina, SectorRutina, TareaRutina, componentesDias, etiquetaDias } from "src/services/rutina";

export const GARNET = "#910434";

const COLOR_ESTADO: Record<EstadoRutina, string> = {
  VIGENTE: "success",
  REEMPLAZADA: "medium",
  FINALIZADA: "primary",
  ANULADA: "medium",
};

const ETIQUETA_ESTADO: Record<EstadoRutina, string> = {
  VIGENTE: "Vigente",
  REEMPLAZADA: "Reemplazada",
  FINALIZADA: "Finalizada",
  ANULADA: "Anulada",
};

export const EstadoRutinaChip: React.FC<{ estado: EstadoRutina }> = ({ estado }) => (
  <IonBadge color={COLOR_ESTADO[estado] || "medium"}>{ETIQUETA_ESTADO[estado] || estado}</IonBadge>
);

export const numeroRutina = (r: { numero: number | null; rutina_id: number; version: number }) =>
  `Rutina N° ${r.numero ?? r.rutina_id}${r.version > 1 ? ` (v${r.version})` : ""}`;

const ListaTareas: React.FC<{ tareas: TareaRutina[]; conFrecuencia?: boolean }> = ({ tareas, conFrecuencia }) => (
  <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
    {tareas.map((t) => (
      <li key={t.tarea_id} style={{ marginBottom: 2 }}>
        {t.nombre}
        {conFrecuencia && t.frecuencia_texto ? (
          <IonText color="medium">
            <small> — {t.frecuencia_texto}</small>
          </IonText>
        ) : null}
      </li>
    ))}
  </ul>
);

/**
 * Muestra la secuencia de la rutina: cada línea con su horario, sector y
 * tareas (diarias y profundas), en orden de ejecución.
 * soloHoy: solo las líneas y tareas que tocan ese día (vista del operario).
 */
const RutinaVista: React.FC<{ sectores: SectorRutina[]; soloHoy?: boolean }> = ({ sectores, soloHoy }) => {
  const visibles = sectores
    .map((s) => ({
      ...s,
      tareas: soloHoy ? (s.hoy === false ? [] : s.tareas.filter((t) => t.hoy)) : s.tareas,
    }))
    .filter((s) => s.tareas.length > 0);

  if (visibles.length === 0) {
    return (
      <IonText color="medium">
        <p>{soloHoy ? "No hay tareas para este día." : "La rutina no tiene líneas cargadas."}</p>
      </IonText>
    );
  }

  // Rutina completa: encabezado por grupo de días (los días que comparten
  // líneas) y etiqueta "Solo ..." en las líneas de algunos días del grupo.
  const diasDe = (x: SectorRutina) => (x.dias_semana?.length ? x.dias_semana : [1, 2, 3, 4, 5, 6, 7]);
  const grupos = soloHoy ? [] : componentesDias(visibles.map(diasDe));
  const grupoDe = (x: SectorRutina) => grupos.find((g) => g.includes(diasDe(x)[0])) || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {visibles.map((s, idx) => {
        const diarias = s.tareas.filter((t) => t.tipo === "DIARIA");
        const profundas = s.tareas.filter((t) => t.tipo === "PROFUNDA");
        const grupo = grupoDe(s);
        const nuevoGrupo = grupos.length > 1 && (idx === 0 || grupoDe(visibles[idx - 1]) !== grupo);
        const parcial = !soloHoy && diasDe(s).length < grupo.length;
        return (
          <React.Fragment key={s.sector_id ?? idx}>
          {nuevoGrupo && (
            <div style={{ background: "#fbf3f5", borderRadius: 6, padding: "4px 10px", marginTop: idx ? 6 : 0 }}>
              <strong style={{ color: GARNET }}>{etiquetaDias(grupo)}</strong>
            </div>
          )}
          <div style={{ borderLeft: `3px solid ${GARNET}`, paddingLeft: 10 }}>
            {s.hora_desde && s.hora_hasta && (
              <div style={{ color: GARNET, fontWeight: 700, fontSize: 13 }}>
                {s.hora_desde} a {s.hora_hasta}
              </div>
            )}
            <strong>{s.nombre}</strong>
            {parcial && (
              <IonBadge color="tertiary" style={{ marginLeft: 6 }}>
                Solo {etiquetaDias(diasDe(s))}
              </IonBadge>
            )}
            {diarias.length > 0 && <ListaTareas tareas={diarias} />}
            {profundas.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <IonText color="primary">
                  <small style={{ fontWeight: 600 }}>Limpieza profunda</small>
                </IonText>
                <ListaTareas tareas={profundas} conFrecuencia={!soloHoy} />
              </div>
            )}
          </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default RutinaVista;
